import { Component } from '@theme/component';

/** Pixels per second — red top bar and trust bar below the hero. */
const TICKER_PX_PER_SEC = 42;

/**
 * @param {HTMLElement | null} track
 */
function syncTickerSpeed(track) {
  if (!track) return;
  const half = Math.max(track.scrollWidth / 2, 1);
  track.style.animationDuration = `${Math.max(8, half / TICKER_PX_PER_SEC)}s`;
}

/**
 * Continuous announcement ticker. Same pixel speed on phone and desktop.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  static SPEED = TICKER_PX_PER_SEC;

  /** @type {HTMLElement | null} */
  #track = null;

  connectedCallback() {
    super.connectedCallback();

    const track = this.querySelector('.announcement-bar__slides');
    if (!track) return;
    this.#track = track;

    if (track.dataset.cloned !== 'true') {
      const originals = [...track.children];
      if (!originals.length) return;

      originals.forEach((slide) => {
        slide.setAttribute('aria-hidden', 'false');
        slide.classList.remove('is-entering', 'is-leaving');
      });

      originals.forEach((slide) => track.append(this.#copySlide(slide)));
      track.dataset.cloned = 'true';
    }

    this.#syncSpeed();
    requestAnimationFrame(this.#syncSpeed);
    window.addEventListener('resize', this.#syncSpeed, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#syncSpeed);
    super.disconnectedCallback();
  }

  #syncSpeed = () => {
    syncTickerSpeed(this.#track);
    document.querySelectorAll('.cm-trust__track').forEach((el) => {
      if (el instanceof HTMLElement) syncTickerSpeed(el);
    });
  };

  /**
   * @param {Element} slide
   */
  #copySlide(slide) {
    const copy = slide.cloneNode(true);
    copy.setAttribute('aria-hidden', 'true');
    copy.removeAttribute('ref');
    return copy;
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}

function syncTrustTracks() {
  document.querySelectorAll('.cm-trust__track').forEach((el) => {
    if (el instanceof HTMLElement) syncTickerSpeed(el);
  });
}

requestAnimationFrame(syncTrustTracks);
window.addEventListener('load', syncTrustTracks, { once: true });
window.addEventListener('resize', syncTrustTracks, { passive: true });
